/**
 * Annuler la validation d'une contre-étude.
 *
 * *« Quand on clique sur annuler la validation il faudrait revenir au stade de la copie
 * modifiable telle qu'elle a été validée puis pouvoir resoumettre à validation »* — Chris,
 * 07/09/2026. Ce qui se défait, c'est la VALIDATION, pas la contre-étude : la copie de travail
 * revient telle quelle et redevient modifiable, seule la CE de suivi part.
 *
 * TROIS PARTIS PRIS D'AFFICHAGE.
 *
 *  - **Le geste s'appelle par ce qu'il fait au métier**, « annuler la validation », et non
 *    « supprimer des versions ».
 *  - **Ce qui revient et ce qui part sont dits SÉPARÉMENT**, avant comme après. Les mêler
 *    annoncerait comme perdu le chiffrage qui est justement rendu — et laisserait dans l'ombre
 *    la seule vraie perte, les affectations réelles saisies sur la CE de suivi : quelqu'un les
 *    a posées à la main, elles ne sont pas du chiffrage, et rien ne les recrée.
 *  - **Le n° d'affaire se retape.** Comme pour la suppression d'un chantier, et pour la même
 *    raison : « êtes-vous sûr ? » n'arrête pas l'erreur de ligne, celle où l'on est sûr — mais
 *    sur le mauvais chantier.
 */
import { useState } from "react";
import type { JSX } from "react";
import { annulerValidation } from "./api";

type Etat =
  | { phase: "repos" }
  | { phase: "confirme" }
  | { phase: "envoi" }
  | { phase: "erreur"; raison: string };

export function AnnulerValidation({
  versionId,
  apo,
  onAnnulee,
}: {
  readonly versionId: string;
  /** Le n° d'affaire attendu — l'écran le montre, le serveur le revérifie. */
  readonly apo: string;
  /** L'identifiant de la copie rendue voyage avec le bilan : c'est sur elle qu'il faut ouvrir. */
  readonly onAnnulee: (bilan: string, versionRendue: string) => void;
}): JSX.Element | null {
  const [etat, setEtat] = useState<Etat>({ phase: "repos" });
  const [saisi, setSaisi] = useState("");

  if (etat.phase === "repos") {
    return (
      <button
        type="button"
        className="lien"
        onClick={() => {
          setEtat({ phase: "confirme" });
        }}
      >
        Annuler la validation…
      </button>
    );
  }

  const envoiEnCours = etat.phase === "envoi";

  const annuler = (): void => {
    if (saisi.trim() === "") return;
    setEtat({ phase: "envoi" });
    annulerValidation(versionId, saisi.trim())
      .then((bilan) => {
        setEtat({ phase: "repos" });
        setSaisi("");
        const { rendue, suiviEfface } = bilan;
        const nom = rendue.rang === null ? rendue.libelle : `V${String(rendue.rang)}`;
        onAnnulee(
          `Validation annulée. ${nom} — ${rendue.libelle} — redevient modifiable telle ` +
            `qu'elle a été validée : ${String(rendue.contenu.postes)} poste(s), ` +
            `${String(rendue.contenu.lignes)} ligne(s), ${String(rendue.contenu.barres)} ` +
            `barre(s). ${String(rendue.demandesRouvertes)} demande(s) de validation remise(s) ` +
            "de côté : le circuit se rouvre à la première étape. " +
            (suiviEfface.existait
              ? `La CE de suivi est effacée, avec ${String(
                  suiviEfface.contenu.affectationsReelles,
                )} affectation(s) réelle(s) : le chantier sort du plan de charge.`
              : "Il n'y avait aucune CE de suivi à effacer."),
          rendue.versionId,
        );
      })
      .catch((erreur: unknown) => {
        setEtat({
          phase: "erreur",
          raison: erreur instanceof Error ? erreur.message : String(erreur),
        });
      });
  };

  return (
    <form
      className="supprimer-version"
      onSubmit={(e) => {
        e.preventDefault();
        annuler();
      }}
    >
      <p>
        Annuler la validation de ce déboursé ? <strong>La copie de travail revient telle qu'elle
        a été validée</strong> et redevient modifiable : son chiffrage et son planning ne
        changent pas. <strong>La CE de suivi est effacée</strong>, avec son planning et{" "}
        <strong>les affectations réelles déjà saisies</strong> — c'est la seule chose que rien
        ne recrée. Le chantier sortira du plan de charge, et la copie rendue pourra être
        retravaillée puis resoumise depuis la première étape du circuit.
      </p>
      <label>
        Retaper le n° d'affaire ({apo}) pour confirmer
        <input
          value={saisi}
          disabled={envoiEnCours}
          autoFocus
          onChange={(e) => {
            setSaisi(e.target.value);
            if (etat.phase === "erreur") setEtat({ phase: "confirme" });
          }}
        />
      </label>
      <div className="supprimer-version__actions">
        <button
          type="submit"
          className="danger"
          disabled={envoiEnCours || saisi.trim() === ""}
        >
          {envoiEnCours ? "Annulation…" : "Annuler la validation"}
        </button>
        <button
          type="button"
          disabled={envoiEnCours}
          onClick={() => {
            setEtat({ phase: "repos" });
            setSaisi("");
          }}
        >
          Renoncer
        </button>
      </div>
      {etat.phase === "erreur" && (
        <p className="alerte" role="alert">
          {etat.raison}
        </p>
      )}
    </form>
  );
}
